import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Badge } from '../ui';
import type { Service, ServiceVariant } from '../../stores/servicesStore';

const formatLengthDisplay = (length: string): string =>
	length.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

export interface ServiceModalProps {
	isOpen: boolean;
	onClose: () => void;
	service?: Service | null;
	onSave: (service: Service) => Promise<void>;
	onDelete?: (id: string) => void;
}

const SIZE_OPTIONS = ['XXS', 'XS', 'S', 'SM', 'M', 'L', 'XL', 'JUMBO'];
const LENGTH_OPTIONS = ['SHOULDER', 'BRA', 'MID_BACK', 'WAIST', 'BUTT', 'HIP', 'THIGH', 'KNEE'];

const ServiceModal = ({ isOpen, onClose, service, onSave, onDelete }: ServiceModalProps) => {
	const isEditMode = !!service;

	// Form state
	const [serviceName, setServiceName] = useState('');
	const [description, setDescription] = useState('');
	const [variants, setVariants] = useState<ServiceVariant[]>([]);

	// Save loading state
	const [isSaving, setIsSaving] = useState(false);

	// Current variant input row
	const [currentSize, setCurrentSize] = useState('');
	const [currentLength, setCurrentLength] = useState('');
	const [currentDuration, setCurrentDuration] = useState('');
	const [currentPrice, setCurrentPrice] = useState('');

	// Reset form when modal opens/closes or service changes
	useEffect(() => {
		if (isOpen) {
			if (service) {
				setServiceName(service.name);
				setDescription(service.description);
				setVariants([...service.variants]);
			} else {
				setServiceName('');
				setDescription('');
				setVariants([]);
			}
			setCurrentSize('');
			setCurrentLength('');
			setCurrentDuration('');
			setCurrentPrice('');
		}
	}, [isOpen, service]);

	const addVariant = () => {
		if (!currentSize || !currentLength) {
			toast.error('Please select both size and length');
			return;
		}

		const duration = parseInt(currentDuration) || 0;
		const price = parseFloat(currentPrice) || 0;

		if (duration <= 0) {
			toast.error('Please enter a valid duration');
			return;
		}
		if (price <= 0) {
			toast.error('Please enter a valid price');
			return;
		}

		// Check for duplicate size+length
		const exists = variants.find(v => v.size === currentSize && v.length === currentLength);
		if (exists) {
			toast.error(`A ${currentSize} / ${formatLengthDisplay(currentLength)} combination already exists`);
			return;
		}

		setVariants([
			...variants,
			{
				id: `temp-${Date.now()}`,
				size: currentSize,
				length: currentLength,
				duration,
				price,
			},
		]);

		// Clear price and duration for quick consecutive adds (keep size/length)
		setCurrentDuration('');
		setCurrentPrice('');
	};

	const removeVariant = (index: number) => {
		setVariants(variants.filter((_, i) => i !== index));
	};

	const handleSave = async () => {
		if (!serviceName.trim()) {
			toast.error('Please enter a service name');
			return;
		}

		if (variants.length === 0) {
			toast.error('Please add at least one pricing combination');
			return;
		}

		const serviceData: Service = {
			id: service?.id || '',
			name: serviceName.trim(),
			description: description.trim(),
			status: service?.status || 'ACTIVE',
			variants,
		};

		setIsSaving(true);
		try {
			await onSave(serviceData);
			onClose();
		} catch {
			// Error toast is handled by the caller
		} finally {
			setIsSaving(false);
		}
	};

	const handleDelete = () => {
		if (!service || !onDelete) return;
		onDelete(service.id);
		onClose();
	};

	const formatDuration = (mins: number): string => {
		const hours = Math.floor(mins / 60);
		const remaining = mins % 60;
		if (hours === 0) return `${remaining}m`;
		if (remaining === 0) return `${hours}h`;
		return `${hours}h ${remaining}m`;
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEditMode ? 'Edit Service' : 'Add New Service'}
			size="xl"
		>
			<div className="space-y-5">
				{/* Service Name */}
				<div className="provider-form__group">
					<label className="provider-form__label">
						Service Name<span className="provider-form__required">*</span>
					</label>
					<input
						type="text"
						className="provider-form__input"
						placeholder="e.g., Box Braids"
						value={serviceName}
						onChange={e => setServiceName(e.target.value)}
					/>
				</div>

				{/* Description */}
				<div className="provider-form__group">
					<label className="provider-form__label">Description</label>
					<textarea
						className="provider-form__input"
						placeholder="Describe this service..."
						rows={2}
						value={description}
						onChange={e => setDescription(e.target.value)}
					/>
				</div>

				{/* Pricing Combinations Section */}
				<div className="service-modal__combinations">
					<h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
						Pricing Combinations<span className="provider-form__required">*</span>
					</h4>

					{/* Input row */}
					<div className="service-modal__input-row">
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
								Size
							</label>
							<select
								className="provider-form__input"
								value={currentSize}
								onChange={e => setCurrentSize(e.target.value)}
							>
								<option value="">Select</option>
								{SIZE_OPTIONS.map(size => (
									<option key={size} value={size}>
										{size}
									</option>
								))}
							</select>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
								Length
							</label>
							<select
								className="provider-form__input"
								value={currentLength}
								onChange={e => setCurrentLength(e.target.value)}
							>
								<option value="">Select</option>
								{LENGTH_OPTIONS.map(length => (
									<option key={length} value={length}>
										{formatLengthDisplay(length)}
									</option>
								))}
							</select>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
								Duration (min)
							</label>
							<input
								type="number"
								className="provider-form__input"
								placeholder="180"
								min={5}
								step={5}
								value={currentDuration}
								onChange={e => setCurrentDuration(e.target.value)}
							/>
						</div>
						<div>
							<label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
								Price ($)
							</label>
							<input
								type="number"
								className="provider-form__input"
								placeholder="0"
								min={0}
								step={0.01}
								value={currentPrice}
								onChange={e => setCurrentPrice(e.target.value)}
							/>
						</div>
						<div className="flex items-end">
							<Button
								variant="primary"
								size="sm"
								onClick={addVariant}
								className="w-full"
							>
								<PlusIcon className="w-4 h-4 mr-1" />
								Add
							</Button>
						</div>
					</div>

					{/* Combinations table */}
					{variants.length > 0 ? (
						<div className="service-modal__table-wrapper">
							<div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
								Added Combinations ({variants.length})
							</div>
							<div className="service-modal__table-scroll">
								<table className="service-modal__table">
									<thead>
										<tr>
											<th>Size</th>
											<th>Length</th>
											<th className="text-right">Duration</th>
											<th className="text-right">Price</th>
											<th className="w-10"></th>
										</tr>
									</thead>
									<tbody>
										{variants.map((variant, idx) => (
											<tr key={variant.id || idx}>
												<td>
													<Badge variant="primary">{variant.size}</Badge>
												</td>
												<td>
													<Badge variant="info">{formatLengthDisplay(variant.length)}</Badge>
												</td>
												<td className="text-right text-sm text-gray-600 dark:text-gray-300">
													{formatDuration(variant.duration)}
												</td>
												<td className="text-right text-sm font-semibold text-green-600 dark:text-green-400">
													${variant.price.toFixed(2)}
												</td>
												<td className="text-center">
													<button
														type="button"
														onClick={() => removeVariant(idx)}
														className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all cursor-pointer"
													>
														<TrashIcon className="w-4 h-4" />
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">
							No combinations added yet. Add your first pricing combination above.
						</div>
					)}
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					{isEditMode && onDelete && (
						<Button
							variant="danger"
							onClick={handleDelete}
							className="sm:mr-auto"
							disabled={isSaving}
						>
							Delete Service
						</Button>
					)}
					<div className="flex flex-col-reverse sm:flex-row gap-3 sm:ml-auto">
						<Button variant="secondary" onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSave} isLoading={isSaving}>
							{isEditMode ? 'Update Service' : 'Add Service'}
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ServiceModal;
